// src/belediye/pages/gorevler/GorevDetayPage.js
// Görev Detayı (/yonetim/gorevler/:taskId) — AYRI SAYFA.
//
// FAZ-64 (2026-07-29) — SAYFA ARTIK "KAYIT"TIR, KUMANDA DEĞİL.
//
// Kök sorun: aynı eylemin İKİ yolu vardı (soldaki düğme + sohbetteki çip). İki yol
// iki görünürlük kuralı demekti; FAZ-61'de kapattığımız yetki sızıntısı bu ikiliğin
// ürünüydü. Kullanıcı kararı: "görev atayan kişi de detay sayfasında ne durumda /
// süre düzenle / dosya ekle / iptal et / yenile düğmelerini görmesin; bunlar zaten
// sohbet ekranından yapılan şeyler."
//
// FAZ-71 (2026-07-29 gece) — SOL SÜTUN TEK BİR KART DİLİ KONUŞUR.
// Kullanıcı: "soldaki kartların hepsi farklı bir dil konuşuyor; hepsini aynı yapıya
// getirmemiz gerekiyor." Artık TEK ilkel var: `<Kart>` — renkli başlık şeridi
// (ikon + etiket + sağ slot) + gövde. Künye, Kimler, Süre, Görev nerede, Sonuç,
// Zaman tüneli ve Sohbette paylaşılanlar hepsi odur; akordeon olanlar başlığı
// <button> olarak çizer. Kart kimliğini TEK değişken taşır: `--gk-kart-ton`.
//
// Yerleşim (sol = kayıt, sağ = sohbet · ikisi AYNI HİZADA başlar):
//   ① KÜNYE       — başlık + açıklama + "Ayrıntılar" akordeonu (VARSAYILAN KAPALI)
//   ② KİMLER      — nizam kişi sayısından doğar (2·3·2×2·2+3·3+3, 6'dan sonra akordeon)
//   ③ ÖLÇÜ ŞERİDİ — Kalan süre (dar) | Görev nerede? (geniş)
//   ④ SONUÇ       — sabit değil, DURUMDAN doğar (onay bekliyor / sonuç / iptal)
//   ⑤ ZAMAN TÜNELİ | SOHBETTE PAYLAŞILANLAR — paylaşılan yoksa tünel TAM GENİŞ
//
// MEDYA TEK YERDE GÖSTERİLİR (kullanıcı: "aynı şey birden fazla yerde görünüyor"):
//   brief  → künye ▸ Ayrıntılar        · teslim → SONUÇ bloğu
//   süreç  → SOHBETTE PAYLAŞILANLAR    · diğerleri o eki TEKRAR çizmez
//
// Medyanın TEK gösterim biçimi var (`MedyaIzgarasi`): kare döşeme, harita 2×2,
// görseller 2'şerli akar. Görsele tıklamak SAYFADAN ÇIKARMAZ — sohbetteki gibi
// büyüteç (modal) açar. Belgede tür rozeti (PDF/DOC/XLS) vardır. Sohbetten gelen
// her öğede 💬 rozeti ilgili mesaja götürür.
//
// Ekler: ham /media/ yolu YOK — gated download ucundan Bearer'lı blob çekilir.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  PaperClipIcon,
  Squares2X2Icon,
  TrashIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import usePermissions from "../../hooks/usePermissions";
import useModuleEnabled from "../../hooks/useModuleEnabled";
import api from "../../services/belediyeApi";
import { toApiRelative } from "../../services/mesajlasmaApi";
import { gotoMessage } from "../../services/mesajlasmaBridge";
import {
  clearTaskDetail,
  fetchTaskDetailThunk,
  selectTaskDetail,
} from "../../redux/slices/internalTasksSlice";
import {
  EmptyState,
  StatusChip,
  dueText,
  stripClass,
} from "../../components/tasks/GorevBits";
import ChatThread from "../../components/mesajlasma/ChatThread";
// FAZ-90: alt barın merkez düğmesi bu sayfada talep sohbetini açar
import { GOREV_SOHBET_EVENT } from "../../components/AltBar";
import useKatman from "../../hooks/useKatman";
// FAZ-88: detay başlığını navbara bildir (dar katmanda orta yuvaya yazılır)
import { useNavbarBasligiAyarla } from "../../components/NavbarBaslikContext";
import useKlavyeAlani from "../../components/mesajlasma/useKlavyeAlani";
// Konum kartı: görevin KENDİ konumu mini harita olarak çizilir. KonumBlok
// haritayı kendi içinde React.lazy ile çeker — react-leaflet'i statik içe
// aktarmak ilgisiz testleri kırıyor (FAZ-55 tuzağı).
import KonumBlok from "../../components/mesajlasma/KonumBlok";
// FAZ-114: "Talebi sil" geri alınamaz görünen bir işlem — onaysız çalışmaz.
import ConfirmDialog from "../../components/mesajlasma/ConfirmDialog";
import { deleteTask } from "../../services/internalTasksApi";
import { toast } from "react-toastify";
import "../../components/tasks/gorev.css";
import "../../components/mesajlasma/chat.css";

const trDateTime = (v) =>
  v
    ? new Date(v).toLocaleString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const formatSize = (n) => {
  if (n === undefined || n === null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

// Kişi kartı avatarı için baş harf(ler).
const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

// Metin karşılaştırması — "aynı bilgi iki kez yazılmasın" kuralının ölçütü.
const norm = (v) =>
  String(v || "")
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    .trim();

// ── Akordeon hafızası ────────────────────────────────────────────────────
// Kullanıcı elle açıp kapatırsa tercihi panel bazında hatırlanır ve varsayılanı
// ezer (`localStorage: gorevDetayAkordeon`).
const AKORDEON_LS = "gorevDetayAkordeon";

const tercihOku = () => {
  try {
    const v = JSON.parse(localStorage.getItem(AKORDEON_LS) || "{}");
    return v && typeof v === "object" ? v : {};
  } catch {
    return {};
  }
};

function useAkordeon(anahtar, varsayilan) {
  const [acik, setAcik] = useState(() => {
    const t = tercihOku();
    return typeof t[anahtar] === "boolean" ? t[anahtar] : varsayilan;
  });
  useEffect(() => {
    const t = tercihOku();
    if (typeof t[anahtar] !== "boolean") setAcik(varsayilan);
  }, [anahtar, varsayilan]);
  const degistir = useCallback(() => {
    setAcik((v) => {
      const yeni = !v;
      try {
        localStorage.setItem(
          AKORDEON_LS,
          JSON.stringify({ ...tercihOku(), [anahtar]: yeni })
        );
      } catch {
        /* depolama yoksa oturumluk kalır */
      }
      return yeni;
    });
  }, [anahtar]);
  return [acik, degistir];
}

// ── KART — SAYFANIN TEK KART DİLİ (FAZ-72: SESSİZ SÜRÜM) ─────────────────
//
// Kullanıcı (2026-07-29): "soldaki kartların hepsi farklı bir dil konuşuyor;
// hepsini aynı yapıya getirmemiz gerekiyor." → tek ilkel doğru karardı.
// Kullanıcı (2026-07-30): "md kart yapmışsın, çok kaba olmuş, çok fazla renk
// var; eski kart mantığı güzeldi." → ilkel KALDI, dili sakinleşti: renkli
// başlık şeridi kalktı, kart baştan sona beyaz, etiket soluk.
//
// RENK KURALI: nötr kartta hiç renk yok. Renk yalnız BİLGİ TAŞIYINCA girer —
// süre sıkışınca ve sonuç bloğunun kimliğinde (onay / teslim / iptal).
function Kart({
  ton = "notr",
  ikon: Ikon,
  baslik,
  altBaslik = null,
  sag = null,
  acik = null, // null → akordeon değil
  onAc = null,
  ekSinif = "",
  govdeSinif = "",
  kokRef = null,
  etiketli = null, // aria-label (region olarak duyurulacaksa)
  children,
}) {
  const akordeon = typeof acik === "boolean" && typeof onAc === "function";
  const ic = (
    <>
      {Ikon && <Ikon className="gk-kart-ikon" aria-hidden="true" />}
      <span className="gk-kart-metin">
        <span className="gk-kart-ad">{baslik}</span>
        {altBaslik && <span className="gk-kart-alt">{altBaslik}</span>}
      </span>
      {sag ? <span className="gk-kart-sag">{sag}</span> : null}
      {akordeon && <span className="gk-kart-ok">{acik ? "⌄" : "›"}</span>}
    </>
  );
  return (
    <section
      className={`gk-kart gk-kart--${ton} ${ekSinif}`.trim()}
      ref={kokRef}
      aria-label={etiketli || undefined}
    >
      {akordeon ? (
        <button type="button" className="gk-kart-bas" onClick={onAc} aria-expanded={acik}>
          {ic}
        </button>
      ) : (
        <header className="gk-kart-bas">{ic}</header>
      )}
      {(!akordeon || acik) && (
        <div className={`gk-kart-govde ${govdeSinif}`.trim()}>{children}</div>
      )}
    </section>
  );
}

// ── MEDYA ────────────────────────────────────────────────────────────────
// Belge türü ROZETİ: kullanıcı "word dosyasıysa DOC, pdf ise PDF ikonu görelim,
// neyin ne olduğunu bilelim" dedi. Uzantıdan türer; mime yalnız görsel/belge
// ayrımında kullanılır (sunucu bazı türlerde octet-stream döndürüyor).
const BELGE_TURLERI = [
  [/\.pdf$/i, "PDF", "pdf"],
  [/\.(docx?|odt|rtf)$/i, "DOC", "doc"],
  [/\.(xlsx?|csv|ods)$/i, "XLS", "xls"],
  [/\.(pptx?|odp)$/i, "PPT", "ppt"],
  [/\.(zip|rar|7z|tar|gz)$/i, "ZIP", "zip"],
  [/\.(mp4|mov|avi|mkv|webm)$/i, "VİDEO", "video"],
  [/\.(mp3|wav|m4a|ogg|aac)$/i, "SES", "ses"],
  [/\.(txt|log|md)$/i, "METİN", "metin"],
];

function belgeTuru(ad) {
  for (const [re, etiket, cls] of BELGE_TURLERI) {
    if (re.test(String(ad || ""))) return { etiket, cls };
  }
  const uzanti = String(ad || "").split(".").pop();
  return {
    etiket: uzanti && uzanti.length <= 4 && uzanti !== ad ? uzanti.toUpperCase() : "DOSYA",
    cls: "diger",
  };
}

// Korumalı görsel: gated download URL'inden Bearer'lı blob → objectURL.
// download_url KÖK-göreli (/api/...) gelir; api baseURL'i zaten .../api içerdiği
// için toApiRelative ile baştaki /api düşürülür (yoksa /api/api → 404).
//
// SUNUM SAFIDIR: tıklamayı ARTIK kendi yakalamaz (eskiden yeni sekme açıyordu —
// kullanıcı: "yeni sayfa açılıyor, halbuki modal açılsın istiyoruz"). Açma işini
// döşemenin kendi düğmesi yapar.
function ProtectedImage({ downloadUrl, alt, className = "gk-ekkare-img" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    api
      .get(`${toApiRelative(downloadUrl)}?inline=1`, { responseType: "blob" })
      .then((resp) => {
        if (cancelled) return;
        revoke = URL.createObjectURL(resp.data);
        setSrc(revoke);
      })
      .catch(() => setSrc(false));
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [downloadUrl]);
  if (src === false) {
    return (
      <span className="gk-ekkare-bos">
        <DocumentIcon className="gk-ic" />
      </span>
    );
  }
  if (!src) return <span className={`${className} is-yukleniyor`} aria-label="Yükleniyor" />;
  return <img className={className} src={src} alt={alt} />;
}

// ── MEDYA TOPLAMA — tek gösterim kuralının veri tarafı ────────────────────
//
// İKİ ayrı depo var ve ikisi de "görev medyası":
//   • TaskAttachment  — görev açılırken/sonradan eklenen dosyalar (task.attachments)
//   • Sohbet ekleri   — mesajlara iliştirilen dosyalar + konum mesajları (task.comments)
//
// Her ek TEK yerde gösterilir; yerini İKİ şey belirler: kim gönderdi (yön) ve
// hangi aşamada (brief / süreç / teslim). Diğer bölümler o eki TEKRAR çizmez.
const TESLIM_ASAMA = "teslim";
const BRIEF_ASAMA = "brief";
const SUREC_ASAMA = "surec";

// Teslim mesajı + hemen öncesindeki AYNI yazara ait gövdesiz ekli mesajlar.
// (G13 sırası: tamamlama sihirbazı önce fotoğrafı, sonra teslim notunu yollar —
// teslim sohbeti kilitlediği için foto ondan ÖNCE gitmek zorunda.)
function teslimMesajIdleri(task) {
  const msgs = task?.comments || [];
  const mid = task?.delivery?.message_id;
  const out = new Set();
  if (!msgs.length) return out;

  let i = mid ? msgs.findIndex((m) => Number(m.id) === Number(mid)) : -1;
  if (i < 0) {
    // FAZ-47 öncesi kayıtlar: id yok → teslim notu metniyle yedek eşleşme.
    const hedef = String(task?.delivery?.note || "").trim();
    if (!hedef) return out;
    for (let k = msgs.length - 1; k >= 0; k -= 1) {
      if (String(msgs[k].body || "").trim() === hedef) {
        i = k;
        break;
      }
    }
  }
  if (i < 0) return out;

  out.add(Number(msgs[i].id));
  const yazar = msgs[i].author;
  for (let k = i - 1; k >= 0; k -= 1) {
    const m = msgs[k];
    const ekler = m.attachments || [];
    if (m.author !== yazar || String(m.body || "").trim() !== "" || ekler.length === 0) break;
    out.add(Number(m.id));
  }
  return out;
}

const turBul = (mime) => ((mime || "").startsWith("image/") ? "gorsel" : "belge");

function toplaMedya(task) {
  const verenId = Number(task?.created_by);
  const teslimSet = teslimMesajIdleri(task);
  const items = [];

  // 1) Görevin kendi konumu — brief konumu (verenin verdiği yer bilgisi).
  if (task?.latitude != null && task?.longitude != null) {
    items.push({
      key: "konum-gorev",
      tur: "konum",
      yon: "veren",
      asama: BRIEF_ASAMA,
      ad: task.address || task.neighborhood_name || "Talep konumu",
      konum: { lat: Number(task.latitude), lng: Number(task.longitude) },
      kim: task.created_by_display,
      tarih: task.created_at,
      messageId: null,
    });
  }

  // 2) TaskAttachment'lar. Yükleyen görevin sahibiyse REFERANS malzemesidir
  //    (brief); başkası yüklediyse süreç ekidir.
  for (const a of task?.attachments || []) {
    const verenMi = Number(a.uploaded_by) === verenId;
    items.push({
      key: `ta-${a.id}`,
      tur: turBul(a.mime_type),
      yon: verenMi ? "veren" : "yapan",
      asama: verenMi ? BRIEF_ASAMA : SUREC_ASAMA,
      ad: a.filename,
      size: a.size,
      downloadUrl: a.download_url,
      kim: a.uploaded_by_display,
      tarih: a.created_at,
      // TaskAttachment'ın mesajı YOKTUR → 💬 rozeti çizilmez (yanıltıcı olurdu).
      messageId: null,
    });
  }

  // 3) Sohbet ekleri + konum mesajları.
  for (const m of task?.comments || []) {
    if (m.is_deleted) continue;
    const verenMi = Number(m.author) === verenId;
    const asama = teslimSet.has(Number(m.id)) ? TESLIM_ASAMA : SUREC_ASAMA;
    const ortak = {
      yon: verenMi ? "veren" : "yapan",
      asama,
      kim: m.author_display,
      tarih: m.created_at,
      messageId: Number(m.id),
    };
    if (m.konum) {
      items.push({
        key: `konum-${m.id}`,
        tur: "konum",
        ad: (m.body || "").replace(/^📍\s*/, "") || "Paylaşılan konum",
        konum: m.konum,
        ...ortak,
      });
    }
    for (const a of m.attachments || []) {
      items.push({
        key: `ma-${a.id}`,
        tur: turBul(a.mime_type),
        ad: a.filename,
        size: a.size,
        downloadUrl: a.download_url,
        ...ortak,
      });
    }
  }
  return items;
}

// ── DAR EKRAN EŞİĞİ — TEK CÜMLE, İKİ DİL ─────────────────────────────────
// FAZ-73 (kullanıcı 2026-07-29): "iPad Pro dikeyde sohbeti göremiyorum, sohbet
// düğmesi de görünmüyor."
//
// KÖK NEDEN: eşik CSS'te ve JS'te AYRI yazılmıştı.
//   CSS : (max-width:1023px), (orientation:portrait) and (max-width:1100px)
//   JS  : (max-width:1023px)
// iPad Pro 12.9" DİKEY genişliği tam 1024px. CSS "dar" der → sohbet sütununu
// gizler; JS "geniş" der → sağ sütunu çizer ve mobil düğmeyi HİÇ basmaz.
// Sonuç: ekranda ne panel ne düğme; sohbete erişimin tek yolu kapanır.
//
// KURAL: bu eşik TEK yerde yaşar ve iki tarafta AYNI metindir. Değişirse
// ikisi birden değişir (nöbetçi: gorev.detay.mobil.test.js).
export const DAR_SORGU =
  "(max-width: 1023px), (orientation: portrait) and (max-width: 1100px)";

// Döşeme sırası: harita → görseller → belgeler. Harita 2×2 kaplar, görseller
// onun yanına 2'şerli akar (kullanıcı tarifi).
const TUR_SIRA_NO = { konum: 0, gorsel: 1, belge: 2 };
const medyaSirala = (liste) =>
  [...liste].sort((a, b) => TUR_SIRA_NO[a.tur] - TUR_SIRA_NO[b.tur]);

export default function GorevDetayPage() {
  const { taskId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const moduleOn = useModuleEnabled("internal_tasks");
  // FAZ-72: `loading` artık okunmuyor — bkz. aşağıdaki `gosterilebilir` notu.
  const { data: task, error } = useSelector(selectTaskDetail);

  /* FAZ-88 — dar katmanda navbarın orta yuvası talebin adını yazar, sol
     yuvası geri okuna döner (yüzen geri düğmesi mobilde artık çizilmiyor). */
  useNavbarBasligiAyarla(
    task ? { ad: task.title || "Talep", no: `Talep #${task.id ?? taskId}` } : null
  );

  // Mobil: sohbet tam-ekran overlay olarak açılır (URL değişmez). Donanım geri
  // tuşu overlay'i kapatsın diye açılışta bir history girdisi itilir.
  const [chatOpen, setChatOpen] = useState(false);
  // FAZ-114 — "Talebi sil" onayı ve gidiş-hâli.
  const [silOnay, setSilOnay] = useState(false);
  const [siliniyor, setSiliniyor] = useState(false);
  // Tek ChatThread örneği: masaüstünde sağ panel, mobilde overlay (CSS ile
  // gizlemek iki kez mount ederdi → çift polling; onun yerine JS ile ayır).
  // Eşik CSS ile AYNI cümle (bkz. DAR_SORGU) — yön değişimini de kapsar, o
  // yüzden ayrıca orientationchange dinlemeye gerek yok.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(DAR_SORGU).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(DAR_SORGU);
    const on = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  /* FAZ-97 madde 4 — `isMobile` DAR katmandır (tablet dahil); geri çipinin
     kaldırılması yalnız TELEFONU ilgilendiriyor. Tablette navbar da geri oku
     taşıyor ama orada yatay alan var ve çip hizayı bozmuyor. */
  const telefon = useKatman() === "telefon";

  useEffect(() => {
    const onPop = () => setChatOpen(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const openChat = useCallback(() => {
    window.history.pushState({ gorevChat: true }, "");
    setChatOpen(true);
  }, []);

  /* FAZ-90 — alt barın MERKEZ düğmesi bu sayfada bağlam değiştirir (K-6):
     genel sohbet yerine BU talebin sohbetini açar. Bar ile sayfa farklı
     ağaçlarda olduğu için köprü olay tabanlı — sistemin sohbet açma dili
     zaten böyle (mesajlasma:toggle / mesajlasma:open). */
  useEffect(() => {
    const on = () => openChat();
    window.addEventListener(GOREV_SOHBET_EVENT, on);
    return () => window.removeEventListener(GOREV_SOHBET_EVENT, on);
  }, [openChat]);
  const closeChat = () => {
    if (window.history.state?.gorevChat) window.history.back(); // popstate → kapanır
    else setChatOpen(false);
  };

  const refresh = () => dispatch(fetchTaskDetailThunk(taskId));

  // FAZ-114 — TALEBİ SİL.
  // Silme başarılıysa sayfada KALINMAZ: talep artık hiçbir listede yok ve
  // burada durmak, yeniden yüklendiğinde 404 verecek bir kabuğu göstermek
  // olurdu. Bu yüzden `refresh()` DEĞİL, listeye dönüş yapılır.
  const talebiSil = async () => {
    if (siliniyor) return;
    setSiliniyor(true);
    try {
      await deleteTask(taskId);
      setSilOnay(false);
      toast.success("Talep silindi.");
      navigate("/yonetim/gorevler", { replace: true });
    } catch (e) {
      // Backend'in ayrıntılı mesajı ("size atanan talep silinemez") burada
      // AYNEN gösterilir — genel bir "işlem yapılamadı" kullanıcıya nedeni
      // söylemezdi ve bu uçta neden çoğu zaman yetki değil, TARAF olur.
      toast.error(e?.response?.data?.detail || "Talep silinemedi.");
      setSiliniyor(false);
    }
  };

  useEffect(() => {
    if (!moduleOn) return undefined;
    dispatch(fetchTaskDetailThunk(taskId));
    return () => dispatch(clearTaskDetail());
  }, [dispatch, taskId, moduleOn]);

  // Sayfa bu iki yüzen düğmeyi KENAR SEKMESİNE çevirir (kullanıcı: "yuvarlak
  // düğme içeriği engelliyor, sağ kenara yapışsın ama kaybolmasın").
  useEffect(() => {
    document.documentElement.setAttribute("data-gk-detay", "1");
    return () => document.documentElement.removeAttribute("data-gk-detay");
  }, []);

  // Büyüteç (görsel modalı) — sohbetteki lightbox ile AYNI yüzey ve aynı
  // sınıflar. Kullanıcı: "resimlere tıklayınca yeni sayfa açılıyor; halbuki
  // sohbetteki gibi modal açılsın."
  const [buyutec, setBuyutec] = useState(null); // { liste, i } | null
  const gorseliAc = useCallback((liste, oge) => {
    const i = Math.max(0, liste.findIndex((x) => x.key === oge.key));
    setBuyutec({ liste, i });
  }, []);

  const sonucRef = useRef(null);
  const kunyeRef = useRef(null);
  // Tam-ekran sohbet açıkken kap YAZILIM KLAVYESİNE göre boyutlanır — yoksa
  // yazı kutusu klavyenin altında kalır (bkz. useKlavyeAlani).
  const overlayRef = useRef(null);
  useKlavyeAlani(chatOpen, overlayRef);

  const medya = useMemo(() => (task ? toplaMedya(task) : []), [task]);

  if (!moduleOn) return <EmptyState>Kurum İçi Talepler modülü kapalı.</EmptyState>;
  // FAZ-72 — TAZELEME SAYFAYI BOŞALTMAZ.
  //
  // Eskiden koşul `loading || …` idi. `loading`, ilk yükte olduğu gibi HER
  // TAZELEMEDE de true olur (fetchTaskDetailThunk.pending) — sohbetten bir
  // görev aksiyonu yapıldığında `onTaskChanged` tam da bunu tetikliyordu.
  // Sonuç: sayfanın TAMAMI yer tutucuyla değişiyor, sağdaki ChatThread DOM'dan
  // sökülüyordu. Sökülünce bileşenin yerel durumu (seçilmiş ama henüz
  // gönderilmemiş dosyalar, yazılan metin, açık sihirbaz) ve GİZLİ DOSYA
  // GİRİŞİ yok oluyor; kullanıcı işletim sisteminin dosya penceresindeyken
  // tazeleme olursa seçilen fotoğrafın `change` olayı sökülmüş bileşene düşüyor
  // → hiçbir istek çıkmıyor, hata da görünmüyor. Kullanıcının tarifi birebir
  // buydu: "sayfa otomatik yenileniyor mu, bir şeyler oluyor ve gitmiyor".
  //
  // Yeni kural: yer tutucu YALNIZ gösterilecek veri yokken. Elde İSTENEN
  // görevin verisi varsa tazeleme sırasında bayat veriyle çizmeye devam
  // ederiz. `task.id !== taskId` kıyası, başka bir göreve geçerken eskisinin
  // verisini göstermeyi engeller (o durumda yer tutucu doğrudur).
  const gosterilebilir = task && String(task.id) === String(taskId);
  if (!gosterilebilir && error) return <EmptyState>{String(error)}</EmptyState>;
  if (!gosterilebilir) return <EmptyState>Talep yükleniyor…</EmptyState>;

  const perms = task.permissions || {};

  const teslim = task.delivery || null;
  const briefMedya = medyaSirala(medya.filter((m) => m.asama === BRIEF_ASAMA));
  const teslimMedya = medyaSirala(medya.filter((m) => m.asama === TESLIM_ASAMA));
  const surecMedya = medyaSirala(medya.filter((m) => m.asama === SUREC_ASAMA));

  const onayNotu =
    task.status === "tamamlandi"
      ? (task.events || [])
          .filter((e) => e.event_type === "completed")
          .slice(-1)[0]?.payload?.note || ""
      : "";

  const downloadAttachment = async (a) => {
    try {
      const resp = await api.get(toApiRelative(a.downloadUrl), { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.ad;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      /* indirme hatası sessiz — kullanıcı tekrar dener */
    }
  };

  // 💬 rozeti / "Sohbette onayla": sohbeti odakla ve ilgili mesaja in.
  // Masaüstünde sohbet zaten sağda → doğrudan kaydır. Mobil/tablette önce
  // overlay açılır, DOM oturduktan sonra köprü olayı yollanır.
  const mesajaGit = (messageId) => {
    if (!messageId || !task.conversation_id) return;
    if (isMobile && !chatOpen) {
      openChat();
      window.setTimeout(() => gotoMessage(task.conversation_id, messageId), 260);
      return;
    }
    gotoMessage(task.conversation_id, messageId);
  };

  const medyaArac = { onIndir: downloadAttachment, onMesaj: mesajaGit, onGorsel: gorseliAc };

  // Sohbet düğümü TEK örnek: masaüstünde sağ panel, mobilde overlay içinde
  // render edilir (ikisi aynı anda mount edilmez — bkz. isMobile).
  /* FAZ-97 madde 13 — MOBİL OVERLAY'DE ÇİFT BAŞLIK VARDI.
     Kullanıcı: "yukarıda 'Talep Sohbeti' yazıyor, sonrasında aşağıda sohbet
     başlığı var; bunu istemiyoruz. Oraya tıpkı normal sohbetteki gibi ilgili
     sohbetin başlığını yazalım, yanına geri butonu."

     Overlay'in kendi çubuğu kalktı; `ChatThread` orada `variant="page"` ile
     çiziliyor ve kendi başlığını kullanıyor (geri · avatar · sohbet adı · ⋮).
     Masaüstünde sağ sütun `variant="task"` olarak KALIYOR: orada başlık zaten
     kartın kendisi, geri okunun anlamı yok.

     Yan etki (bilinçli): `variant="page"` görev şeridini de açıyor
     (`taskDetail && variant !== "task"`). Overlay o talebin sohbeti ve şerit
     talebin durumunu taşıyor — "normal sohbetteki gibi olsun" cümlesiyle
     uyumlu. */
  const chatGovde = (varyant, onBack) =>
    task.mesajlasma_enabled !== false && task.conversation_id ? (
      <div className="msj-task-panel">
        <ChatThread
          conversationId={task.conversation_id}
          initialTitle={task.title}
          variant={varyant}
          onBack={onBack}
          onTaskChanged={refresh}
        />
      </div>
    ) : (
      <Kart ikon={ChatBubbleLeftRightIcon} baslik="Talep sohbeti">
        <EmptyState>
          {task.mesajlasma_enabled === false
            ? "Mesajlaşma modülü kapalı."
            : "Bu talep için henüz yazışma başlamadı. İlk mesajı siz yazın."}
        </EmptyState>
      </Kart>
    );

  // Masaüstü sağ sütunu: eski davranış birebir.
  const chatNode = chatGovde("task");

  return (
    <div className="gk-page gk-page--detay">
      {/* Geri dönüş ARTIK ÇİP (kullanıcı: "bunu bir çip haline al ve daha güzel
          yaz"). Sabit yükseklikli bir satırdır: sohbet sütununun yapışma eşiği
          bu satırın boyundan hesaplanıyor, içerik büyüyünce hiza kaymasın. */}
      {/* FAZ-97 madde 4 — TELEFONDA ÇİZİLMEZ.
          Kullanıcı: "sayfa içeriğinde geri dön var ya, burada mobilde buna
          gerek yok, gereksiz olmuş."
          Haklı: navbarın sol yuvası bu rotada zaten geri oku (FAZ-88/92) ve
          detay sayfasında navbar ASILI duruyor (FAZ-97 madde 2 teyidi) — iki
          geri yolu üst üste biniyordu.
          CSS ile gizlemek yerine render etmemek: `.gk-detail-ust` sohbet
          sütununun yapışma eşiğini besleyen bir ÖLÇÜ satırı; boş bırakmak
          34+12px'lik ölü boşluk bırakırdı. */}
      {!telefon && (
        <div className="gk-detail-ust">
          <button
            type="button"
            className="gk-geri-cip"
            onClick={() => navigate("/yonetim/gorevler")}
          >
            <ArrowLeftIcon className="gk-ic" /> Talep listesine dön
          </button>
        </div>
      )}

      <div className="gk-detail-split">
        {/* ── SOL: görevin KAYDI (kumanda yok) ─────────────────────────── */}
        <div className="gk-detail-info">
          <KunyeKarti
            task={task}
            kokRef={kunyeRef}
            brief={briefMedya}
            arac={medyaArac}
            onBasvuru={
              task.application_summary && can("applications.view")
                ? () => navigate(`/yonetim/basvurular/${task.application_summary.id}`)
                : null
            }
          />

          <KisiKartlari task={task} />

          <div className="gk-olcu">
            <SureKarti task={task} />
            <DurumAkisi task={task} />
          </div>

          {/* SONUÇ — SABİT DEĞİL, durumdan doğar. Görev sürerken hiç çizilmez:
              "teslim edildi mi?" sorusuna Görev nerede? şeridi zaten cevap
              veriyor, boş kutu gürültü olurdu. */}
          <SonucBlogu
            task={task}
            kokRef={sonucRef}
            teslim={teslim}
            medya={teslimMedya}
            onayNotu={onayNotu}
            perms={perms}
            arac={medyaArac}
            onMesaj={mesajaGit}
          />

          {/* Sohbette paylaşılan bir şey YOKSA tünel TAM GENİŞ olur (kullanıcı:
              "zaman tüneli tam geniş olsun; ne zaman ki sohbete konum, belge
              veya resim eklendi, o zaman kısalsın ve yanında görelim"). */}
          <div className={`gk-ikili ${surecMedya.length === 0 ? "gk-ikili--tek" : ""}`.trim()}>
            <ZamanTuneliPanel task={task} />
            <PaylasilanlarPanel medya={surecMedya} arac={medyaArac} />
          </div>

          {/* FAZ-114 — TALEBİ SİL.
              YERİ bilinçli: kaydın EN ALTI. İptal etme "Talebi iptal et" çipiyle
              sohbetin kumanda satırında durur, çünkü o bir iş-akışı adımıdır ve
              sık kullanılır. Silme ise akışın parçası değil, kaydın kendisini
              ekrandan kaldırmaktır — gezinme düğmelerinin ve sık basılan
              çiplerin yanında durmamalı. Sayfanın sonu, "buraya kadar okudum"
              noktasıdır.
              KAPISI backend'in `can_delete` bayrağıdır; "kurucu muyum" hesabı
              burada TEKRARLANMAZ (yoksa "kendisine atanan talep silinemez"
              kuralı iki yerde ayrı ayrı yaşar ve er geç ayrışırlar). */}
          {perms.can_delete && (
            <div className="gk-tehlike">
              <div className="gk-tehlike-metin">
                <b>Talebi sil</b>
                <small>
                  Talep ve sohbeti tüm listelerden kalkar. Kayıt, denetim için
                  saklanır; gerekirse yönetici geri alabilir.
                </small>
              </div>
              <button
                type="button"
                className="gk-tehlike-btn"
                onClick={() => setSilOnay(true)}
              >
                <TrashIcon className="gk-ic" /> Sil
              </button>
            </div>
          )}
        </div>

        {/* ── SAĞ: bu göreve ait yazışma (masaüstünde tam boy panel) ── */}
        {!isMobile && <div className="gk-detail-chat">{chatNode}</div>}
      </div>

      {/* MOBİL / DİKEY TABLET: tam-ekran sohbet overlay'i.
          FAZ-94 madde 23.3 — KÖŞEDEKİ SOHBET DÜĞMESİ SİLİNDİ.
          Kullanıcı: "sayfadan köşedeki sohbet butonunu kaldıralım."
          Bu sayfada İKİ sohbet çağrısı vardı: alt barın altın merkez düğmesi
          ve `.gk-chatfab`. İkisi de aynı `openChat`i çağırıyordu, üstelik köşe
          düğmesi `bottom: 76px` ile tam barın üstünde duruyordu — yani yan yana
          iki düğme, biri diğerinin gölgesinde.
          Masaüstünde bir şey kaybolmadı: `.gk-chatfab` zaten `display:none` ile
          başlıyor ve yalnız dar katmanda açılıyordu; orada sohbet sağ sütunda. */}
      {isMobile && (
        <>
          {chatOpen && (
            <div
              className="gk-chat-overlay"
              role="dialog"
              aria-label="Talep sohbeti"
              ref={overlayRef}
            >
              {/* FAZ-97 madde 13 — kendi başlık çubuğu YOK; başlık ve geri
                  oku ChatThread'in kendi barından geliyor. */}
              <div className="gk-chat-overlay-body">
                {chatGovde("page", closeChat)}
              </div>
            </div>
          )}
        </>
      )}

      {/* FAZ-114 — silme onayı. `.msj-confirm` `position:absolute; inset:0` ile
          çizilir; bu sayfada onu saracak konumlandırılmış bir kap yok (sohbette
          `.msj-task-panel` vardı). Sabit bir kapsayıcı verilmezse diyalog en
          yakın konumlandırılmış ataya yapışır ve sayfanın ortasında değil,
          rastgele bir yerde belirirdi. */}
      {silOnay && (
        <div className="gk-sil-onay">
          <ConfirmDialog
            open
            title="Talebi sil"
            text={
              <>
                <b>{task.title}</b> talebi ve sohbeti tüm listelerden kalkacak.
                Kayıt denetim için saklanır, gerekirse yönetici geri alabilir.
                Devam edilsin mi?
              </>
            }
            confirmLabel={siliniyor ? "Siliniyor…" : "Sil"}
            onConfirm={talebiSil}
            onCancel={() => !siliniyor && setSilOnay(false)}
          />
        </div>
      )}

      {buyutec && (
        <Buyutec
          liste={buyutec.liste}
          i={buyutec.i}
          onKapat={() => setBuyutec(null)}
          onDegis={(i) => setBuyutec((b) => ({ ...b, i }))}
          onIndir={downloadAttachment}
        />
      )}
    </div>
  );
}

// ── BÜYÜTEÇ — sohbetteki lightbox'ın AYNISI (aynı sınıflar) ──────────────
// Ayrı bir görünüm icat edilmedi: kullanıcı "sohbetteki gibi olsun" dedi ve
// chat.css bu sayfada zaten yüklü. Ok tuşları + Escape çalışır.
function Buyutec({ liste, i, onKapat, onDegis, onIndir }) {
  const oge = liste[i];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onKapat();
      if (e.key === "ArrowRight" && liste.length > 1) onDegis((i + 1) % liste.length);
      if (e.key === "ArrowLeft" && liste.length > 1) onDegis((i - 1 + liste.length) % liste.length);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [i, liste.length, onKapat, onDegis]);

  if (!oge) return null;
  return (
    <div className="msj-lightbox" role="dialog" aria-modal="true" aria-label={oge.ad}>
      <div className="msj-lightbox-bar">
        <span className="msj-lightbox-name">
          {oge.ad}
          {liste.length > 1 && (
            <span className="msj-lightbox-sira">
              {i + 1}/{liste.length}
            </span>
          )}
        </span>
        <button type="button" className="msj-lightbox-btn" onClick={() => onIndir(oge)}>
          <ArrowDownTrayIcon /> İndir
        </button>
        <button
          type="button"
          className="msj-lightbox-btn"
          onClick={onKapat}
          aria-label="Kapat"
        >
          <XMarkIcon />
        </button>
      </div>
      <ProtectedImage
        downloadUrl={oge.downloadUrl}
        alt={oge.ad}
        className="msj-lightbox-img"
      />
      {liste.length > 1 && (
        <>
          <button
            type="button"
            className="msj-lightbox-nav is-prev"
            aria-label="Önceki görsel"
            onClick={() => onDegis((i - 1 + liste.length) % liste.length)}
          >
            ‹
          </button>
          <button
            type="button"
            className="msj-lightbox-nav is-next"
            aria-label="Sonraki görsel"
            onClick={() => onDegis((i + 1) % liste.length)}
          >
            ›
          </button>
        </>
      )}
    </div>
  );
}

// ── MEDYA IZGARASI — sayfadaki TEK ek gösterimi ──────────────────────────
//
// Kullanıcı tarifi: "harita varsa kare şeklinde küçük olur, yanına dört tane
// iki-iki şeklinde resim sığar; resimler böyle sıralanmaz, iki iki iki altlı
// üstlü olur." → 4 sütunlu kare döşeme; harita 2×2 kaplar, görseller yanına
// 2'şerli akar. Aynı ızgara künyede, paylaşılanlarda ve sonuçta kullanılır.
function MedyaIzgarasi({ ogeler, arac, ekSinif = "" }) {
  const gorseller = useMemo(() => ogeler.filter((m) => m.tur === "gorsel"), [ogeler]);
  if (!ogeler.length) return null;
  return (
    <div className={`gk-ekgrid ${ekSinif}`.trim()}>
      {ogeler.map((m) => (
        <MedyaKare key={m.key} m={m} grup={gorseller} arac={arac} />
      ))}
    </div>
  );
}

// 💬 rozeti YALNIZ mesajı olan öğede çizilir — TaskAttachment'ın mesajı yoktur,
// oraya rozet koymak "bu dosya şu konuşmadan geldi" diye yalan söylemek olurdu.
function MesajRozeti({ messageId, onMesaj }) {
  if (!messageId) return null;
  return (
    <button
      type="button"
      className="gk-medya-rozet"
      title="Sohbette bu mesaja git"
      aria-label="Sohbette bu mesaja git"
      onClick={(e) => {
        e.stopPropagation();
        onMesaj(messageId);
      }}
    >
      <ChatBubbleOvalLeftEllipsisIcon />
    </button>
  );
}

function MedyaKare({ m, grup, arac }) {
  const { onIndir, onMesaj, onGorsel } = arac;

  // KONUM — iki hâli var ve karar VERİDEN gelir:
  //   • sohbetten geldiyse (messageId var) → tıklayınca o mesaja gider; sayfada
  //     ikinci bir canlı harita açılmaz (kullanıcı: "konuma tıklayınca direkt
  //     mesajdaki yere gitsek de olur").
  //   • görevin KENDİ konumu ise (mesajı yok) → mini harita olarak çizilir,
  //     kendi tam-ekran düğmesiyle büyür.
  if (m.tur === "konum") {
    if (m.messageId) {
      return (
        <span className="gk-ekkare gk-ekkare--konum">
          <button
            type="button"
            className="gk-ekkare-yuzey"
            onClick={() => onMesaj(m.messageId)}
            title={`${m.ad} — sohbette göster`}
          >
            <MapPinIcon className="gk-ekkare-ic" />
            <span className="gk-ekkare-ad">{m.ad}</span>
          </button>
          <MesajRozeti messageId={m.messageId} onMesaj={onMesaj} />
        </span>
      );
    }
    return (
      <span className="gk-ekkare gk-ekkare--harita" title={m.ad}>
        <KonumBlok enlem={m.konum.lat} boylam={m.konum.lng} />
      </span>
    );
  }

  if (m.tur === "gorsel") {
    return (
      <span className="gk-ekkare gk-ekkare--gorsel">
        <button
          type="button"
          className="gk-ekkare-yuzey"
          onClick={() => onGorsel(grup, m)}
          title={`${m.ad}${m.size ? ` · ${formatSize(m.size)}` : ""} — büyüt`}
          aria-label={`${m.ad} — büyüt`}
        >
          <ProtectedImage downloadUrl={m.downloadUrl} alt={m.ad} />
        </button>
        <MesajRozeti messageId={m.messageId} onMesaj={onMesaj} />
      </span>
    );
  }

  const tur = belgeTuru(m.ad);
  return (
    <span className={`gk-ekkare gk-ekkare--belge gk-belge--${tur.cls}`}>
      <button
        type="button"
        className="gk-ekkare-yuzey"
        onClick={() => onIndir(m)}
        title={`${m.ad}${m.size ? ` · ${formatSize(m.size)}` : ""} — indir`}
      >
        <span className="gk-belge-rozet">{tur.etiket}</span>
        <span className="gk-ekkare-ad">{m.ad}</span>
        {m.size ? <span className="gk-ekkare-boy">{formatSize(m.size)}</span> : null}
      </button>
      <MesajRozeti messageId={m.messageId} onMesaj={onMesaj} />
    </span>
  );
}

// ── ① KÜNYE — başlık + açıklama + AYRINTILAR akordeonu (ayrı panel YOK) ──
//
// Kullanıcı: "ayrıntı alanını ayrı vermeyelim, 1. alanın içinde olmalı" ve
// "aynı yerde birden fazla yazı yazmasın — iki yerde mahalle, iki yerde birim".
function KunyeKarti({ task, kokRef, brief, arac, onBasvuru }) {
  const satirlar = ayrintiSatirlari(task);
  // FAZ-74 — AKORDEON YALNIZ METİN SATIRLARI İÇİN AÇILIR.
  // Eskiden `brief.length > 0` de akordeonu doğuruyordu; malzeme akordeonun
  // İÇİNDE olduğu için, yalnız fotoğraflı bir hızlı görevde kullanıcı KAPALI
  // bir "Ayrıntılar" satırı görüyor ve içinde ne olduğunu bilmiyordu.
  const varMi = satirlar.length > 0;
  // 2026-07-29 (kullanıcı vazgeçti): ayrıntılar ARTIK HER ZAMAN KAPALI gelir —
  // görev açık olsa da. "Bu ayrıntılar kısmı her zaman kapalı gelsin, tıklanınca
  // açılır." Elle verilen tercih yine ezer.
  const [acik, degistir] = useAkordeon("ayrinti", false);

  return (
    <Kart
      ikon={ClipboardDocumentListIcon}
      baslik="Talep"
      kokRef={kokRef}
      ekSinif="gk-brief"
      sag={
        <>
          <StatusChip task={task} />
          {task.priority !== "normal" && (
            <span className={`gk-priority gk-priority--${task.priority}`}>
              {task.priority_display}
            </span>
          )}
        </>
      }
    >
      {/* Kaynak rozeti ("Başkan") ve görev numarası ÇIKTI — kullanıcı: "başkanın
          adını buraya yazmam gerekmiyor, #111 gibi numara gereksiz bilgi." */}
      <h1 className="gk-brief-title">{task.title}</h1>
      {task.description ? (
        <div className="gk-brief-desc">{task.description}</div>
      ) : (
        <div className="gk-brief-desc gk-brief-desc--empty">Açıklama girilmemiş.</div>
      )}

      {/* ── FAZ-74 — GÖREVİ VERENİN MALZEMESİ AKORDEONUN DIŞINDA ──────────
          Kullanıcı (2026-07-29): "tam ekranda hâlâ göremiyoruz."
          ÖLÇÜM (gerçek tarayıcı, görev 128, 1512×982, hiçbir şeye tıklamadan):
            açılışta görünen görsel sayısı .......... 0
            "Ayrıntılar"a tıklandıktan sonra ........ 8
          Sebep: kroki/fotoğraf/konum `MedyaIzgarasi` olarak `.gk-inacc-body`
          içinde çiziliyordu ve akordeon —kullanıcının kendi kararıyla— HER
          ZAMAN KAPALI açılıyor. Kapalı satırın özeti de yalnız Mahalle/Adres
          yazıyor; "içeride 2 fotoğraf var" diye bir iz YOK. Yani dosya
          gidiyordu, sayfa göstermiyordu.
          Karar: akordeon tercihi KORUNUR (kapalı gelmeye devam eder), ama
          malzeme oraya AİT DEĞİL — görevli işe başlarken neye bakacağını
          tıklamadan görmeli. Izgara künyenin gövdesine, açıklamanın altına
          alındı. İkinci bir ızgara AÇILMADI (FAZ-71 "tek medya ızgarası"):
          aynı bileşen, aynı sınıf, yalnız yeri değişti. */}
      {brief.length > 0 && (
        <MedyaIzgarasi ogeler={brief} arac={arac} ekSinif="gk-ekgrid--brief" />
      )}

      {/* HIZLI GÖREVDE HİÇ ÇİZİLMEZ — başlık da, ok da, çizgi de yok.
          Kullanıcı boş bir kapağı açmaya çalışmasın. */}
      {varMi && (
        <div className="gk-inacc">
          <button
            type="button"
            className="gk-inacc-head"
            onClick={degistir}
            aria-expanded={acik}
          >
            <span className="gk-inacc-ok">{acik ? "⌄" : "›"}</span> Ayrıntılar
            {!acik && <span className="gk-inacc-ozet">{ayrintiOzeti(satirlar)}</span>}
          </button>
          {acik && (
            <div className="gk-inacc-body">
              {satirlar.map((s) => (
                <div className="gk-kv2" key={s.k}>
                  <span>{s.k}</span>
                  {s.basvuru && onBasvuru ? (
                    <button type="button" className="gk-kv2-link" onClick={onBasvuru}>
                      {s.v}
                    </button>
                  ) : (
                    <b>{s.v}</b>
                  )}
                </div>
              ))}
              {/* FAZ-74: malzeme ızgarası BURADAN ÇIKTI — künyenin gövdesine,
                  açıklamanın hemen altına taşındı (yukarıdaki nota bak). */}
            </div>
          )}
        </div>
      )}
    </Kart>
  );
}

// "Aynı bilgi iki kez yazılmaz" kuralı burada uygulanır.
function ayrintiSatirlari(task) {
  const out = [];
  const yazilan = new Set();
  const ekle = (k, v, extra = {}) => {
    const t = String(v || "").trim();
    if (!t || yazilan.has(norm(t))) return;
    yazilan.add(norm(t));
    out.push({ k, v: t, ...extra });
  };

  ekle("Birim", task.unit_name);

  // Çoğul tekili KAPSAR — ikisi birden yazılmaz.
  const mahalleler = (task.neighborhood_names || []).filter(Boolean);
  const mahalleMetni = mahalleler.length ? mahalleler.join(", ") : task.neighborhood_name;
  const adres = String(task.address || "").trim();
  // Adres zaten mahalle adıyla başlıyorsa mahalle satırı ayrıca yazılmaz.
  const adresMahalleyiIceriyor =
    adres && mahalleler.some((m) => norm(adres).startsWith(norm(m)));
  if (!adresMahalleyiIceriyor) ekle("Mahalle", mahalleMetni);
  ekle("Adres", adres);

  ekle("Vatandaş", task.citizen_summary?.name);
  // Vatandaş notu görev açıklamasının aynısıysa tekrar edilmez.
  if (norm(task.citizen_note) !== norm(task.description)) {
    ekle("Vatandaş notu", task.citizen_note);
  }
  if (task.cancel_reason && task.status === "iptal_edildi") {
    ekle("İptal nedeni", task.cancel_reason);
  }
  const app = task.application_summary;
  if (app) {
    // Başvuru konusu görev başlığıyla aynıysa yalnız NUMARA gösterilir.
    const ayniMi = norm(app.subject) === norm(task.title);
    out.push({
      k: "Bağlı başvuru",
      v: ayniMi ? `#${app.tracking_number}` : `#${app.tracking_number} — ${app.subject}`,
      basvuru: true,
    });
  }
  return out;
}

// Kapalı akordeon başlığının taşıdığı özet — bilgi tamamen kaybolmasın.
function ayrintiOzeti(satirlar) {
  return satirlar
    .filter((s) => ["Birim", "Mahalle", "Bağlı başvuru"].includes(s.k))
    .map((s) => (s.k === "Bağlı başvuru" ? s.v.split(" — ")[0] : s.v))
    .join(" · ");
}

// ── ② SÜRE KARTI ─────────────────────────────────────────────────────────
//
// SAYAÇ YALNIZ SON GÜN ÇALIŞIR (kullanıcı 2026-07-29: "24 gün falan yazıyor,
// altında sayaç açılmış, bu çok saçma; sayacı son gün başlatalım"). 24 saatin
// üstünde kart GÜN sayar (dakikada bir tazelenir, pil yakmaz); son 24 saate
// girince ss:dd:ss saniye saniye akar, son 2 saatte kırmızıya döner.
//
// İkon kartın BAŞLIK ŞERİDİNDE durur, sayının yanında değil (kullanıcı: "sayaç
// yanındaki saat ikonuyla uyuşmuyor") — bu zaten sayfanın ortak kart dili.
function SureKarti({ task }) {
  const [, setTick] = useState(0);
  const acik = Boolean(task?.due_date && task?.is_open);

  const hedefMs = useMemo(() => {
    if (!task?.due_date) return null;
    const saat = task.due_time ? String(task.due_time).slice(0, 5) : null;
    return new Date(`${task.due_date}T${saat ? `${saat}:00` : "23:59:00"}`).getTime();
  }, [task?.due_date, task?.due_time]);

  const kalanMs = hedefMs == null ? null : hedefMs - Date.now();
  // Son gün → 1 sn; öncesinde 60 sn yeter (ekranda değişen tek şey GÜN sayısı).
  const sonGun = kalanMs != null && kalanMs > 0 && kalanMs < 86400000;
  useEffect(() => {
    if (!acik) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), sonGun ? 1000 : 60000);
    return () => clearInterval(id);
  }, [acik, sonGun]);

  const cls = task?.due_date ? stripClass(task) : "gk--normal";
  let ekCls = "";
  let govde = null;
  let altBilgi = null;

  if (!task?.due_date) {
    govde = <span className="gk-sure-durum">Süre yok</span>;
  } else {
    const saat = task.due_time ? String(task.due_time).slice(0, 5) : null;
    const teslimTarihi = new Date(`${task.due_date}T00:00:00`).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "short",
    });
    altBilgi = `${teslimTarihi}${saat ? ` · ${saat}` : ""}`;

    if (task.status === "tamamlandi") {
      govde = <span className="gk-sure-durum">Tamamlandı</span>;
    } else if (task.status === "iptal_edildi") {
      govde = <span className="gk-sure-durum">İptal edildi</span>;
    } else if (task.status === "onay_bekliyor") {
      govde = <span className="gk-sure-durum">Onay bekliyor</span>;
    } else if (task.is_overdue) {
      govde = <span className="gk-sure-durum">{dueText(task)}</span>;
      ekCls = "gk-sure--gecti";
    } else if ((kalanMs ?? 0) <= 0) {
      govde = <span className="gk-sure-durum">Süre doldu</span>;
      ekCls = "gk-sure--gecti";
    } else if (sonGun) {
      const t = Math.floor(kalanMs / 1000);
      const p = (n) => String(n).padStart(2, "0");
      govde = (
        <span className="gk-sure-sayac">
          {`${p(Math.floor(t / 3600))}:${p(Math.floor((t % 3600) / 60))}:${p(t % 60)}`}
        </span>
      );
      ekCls = kalanMs < 7200000 ? "gk-sure--kritik" : "gk-sure--uyari";
    } else {
      const gun = Math.ceil(kalanMs / 86400000);
      govde = (
        <span className="gk-sure-gun">
          {gun} <small>gün</small>
        </span>
      );
    }
  }

  return (
    <Kart
      ikon={ekCls === "gk-sure--kritik" || ekCls === "gk-sure--gecti" ? ExclamationTriangleIcon : ClockIcon}
      baslik={task?.is_open ? "Kalan süre" : "Son teslim"}
      ekSinif={`gk-surecard ${cls} ${ekCls}`.trim()}
      govdeSinif="gk-sure-govde"
    >
      {govde}
      {altBilgi && <div className="gk-sure-alt">{altBilgi}</div>}
    </Kart>
  );
}

// ── ② DURUM AKIŞI — şerit ileri gider ────────────────────────────────────
//
// SAPMA ROZETİ KALKTI (kullanıcı 2026-07-29): "görev yeniden açılmışsa ya da
// iptal edilmişse orası da görünüyor; bunu görmemize gerek yok, zaten zaman
// tünelinde görebiliyoruz." Şeridin kendisi zaten üç şeyi söylüyor: nereye
// gelindi (dolu adımlar), iptal (✕ uç) ve düzeltme (↩ uç). Geri kalanı — kim,
// ne zaman, hangi gerekçeyle — zaman tünelinin işi.
const ADIMLAR = ["Oluşturuldu", "Başlandı", "Onay", "Bitti"];

function DurumAkisi({ task }) {
  const status = task?.status;
  const iptal = status === "iptal_edildi";
  const bitti = status === "tamamlandi";

  const gelinen = bitti
    ? 3
    : status === "onay_bekliyor"
    ? 2
    : ["devam_ediyor", "geri_bildirim_bekliyor"].includes(status)
    ? 1
    : 0;
  const iptalNoktasi = Math.min(gelinen + 1, 3);
  // Düzeltme istendiyse onay adımına ↩ girer — bilgi rozette değil ŞERİTTE.
  const duzeltmeVar =
    !iptal &&
    !bitti &&
    (task?.events || []).some((e) => e.event_type === "changes_requested") &&
    gelinen < 2;

  return (
    <Kart
      ikon={Squares2X2Icon}
      baslik="Talep nerede?"
      ekSinif={`gk-durumcard ${bitti ? "gk--done" : ""}`.trim()}
    >
      <div className="gk-steps">
        {ADIMLAR.map((label, i) => {
          let cls = "";
          let ic = i + 1;
          if (iptal && i === iptalNoktasi) {
            cls = "gk-step--iptal";
            ic = "✕";
          } else if (iptal && i > iptalNoktasi) {
            cls = "gk-step--pasif";
          } else if (bitti || i < gelinen) {
            cls = "is-done";
            ic = "✓";
          } else if (!iptal && i === gelinen) {
            cls = status === "onay_bekliyor" ? "gk-step--onay" : "is-active";
          } else if (duzeltmeVar && i === 2) {
            cls = "gk-step--geri";
            ic = "↩";
          }
          return (
            <div key={label} className={`gk-step ${cls}`}>
              <span className="gk-step-dot">{ic}</span>
              <span className="gk-step-label">
                {iptal && i === iptalNoktasi ? "İptal edildi" : label}
              </span>
            </div>
          );
        })}
      </div>
    </Kart>
  );
}

// ── ③ KİMLER ─────────────────────────────────────────────────────────────
//
// NİZAM KİŞİ SAYISINDAN DOĞAR (kullanıcı 2026-07-29):
//   2 → yan yana · 3 → aynı satırda üç · 4 → 2+2 · 5 → 2 üstte 3 altta ·
//   6 → 3+3 · 6'DAN FAZLA → ilk altı görünür, gerisi akordeonla açılır.
// Sıra sabit: GÖREVİ VEREN en üstte, sonra sorumlular, sonra katılımcılar,
// en sonda takipçiler.
const PARTICIPANT_LABEL = {
  sorumlu: "Sorumlu",
  katilimci: "Katılımcı",
  takipci: "Takipçi",
};
const ROL_SIRA = { sorumlu: 0, katilimci: 1, takipci: 2 };

function KisiKartlari({ task }) {
  const [hepsi, setHepsi] = useState(false);

  const atananlar = (task?.assignments || [])
    .filter(
      (a) =>
        a.is_active &&
        !(a.assignee_user && Number(a.assignee_user) === Number(task?.created_by))
    )
    .sort(
      (a, b) =>
        (ROL_SIRA[a.participant_type] ?? 9) - (ROL_SIRA[b.participant_type] ?? 9)
    );

  const kisiler = [
    {
      key: "veren",
      rol: "Talebi veren",
      ad: task?.created_by_display || "—",
      alt: trDateTime(task?.created_at),
      sinif: "gk-person--veren",
    },
    ...(atananlar.length === 0
      ? [{ key: "yok", rol: "Sorumlu", ad: "Henüz atanmadı", doer: true }]
      : atananlar.map((a) => {
          const isUnit = !a.assignee_user;
          const name = isUnit ? a.assignee_unit_name : a.assignee_user_display;
          return {
            key: a.id || `${a.participant_type}-${name}`,
            rol: isUnit ? "Birim havuzu" : PARTICIPANT_LABEL[a.participant_type] || "Atanan",
            ad: name || "—",
            doer: true,
          };
        })),
  ];

  const TASMA = 6;
  const gorunen = kisiler.length > TASMA ? kisiler.slice(0, TASMA) : kisiler;
  const artan = kisiler.length > TASMA ? kisiler.slice(TASMA) : [];

  const kart = (k) => (
    <div className={`gk-person ${k.sinif || ""}`.trim()} key={k.key}>
      <div className={`gk-person-av ${k.doer ? "gk-person-av--doer" : ""}`.trim()}>
        {k.ad === "Henüz atanmadı" ? "?" : initials(k.ad)}
      </div>
      <div className="gk-person-main">
        <div className="gk-person-role">{k.rol}</div>
        <div className="gk-person-name">{k.ad}</div>
        {k.alt && <div className="gk-person-sub">{k.alt}</div>}
      </div>
    </div>
  );

  return (
    <Kart
      ikon={UserGroupIcon}
      baslik="Kimler"
      sag={<span className="gk-kart-sayi">{kisiler.length}</span>}
    >
      <div className={`gk-people gk-people--${gorunen.length}`}>
        {gorunen.map(kart)}
      </div>
      {artan.length > 0 && (
        <>
          {hepsi && <div className="gk-people gk-people--3 gk-people--devam">{artan.map(kart)}</div>}
          <button
            type="button"
            className="gk-kart-more"
            onClick={() => setHepsi((v) => !v)}
            aria-expanded={hepsi}
          >
            {hepsi ? "Daha az göster" : `+${artan.length} kişi daha`}
          </button>
        </>
      )}
    </Kart>
  );
}

// ── ④ SONUÇ BLOĞU — üç kimlik, durumdan doğar ────────────────────────────
//
// FAZ-71'de SIKILAŞTIRILDI (kullanıcı: "çok geniş gelmiş; en sağda görseller
// çok minik görünsün, yanında onay notu — daha nizami olsun"). Gövde iki
// sütun: solda kim + not, sağda küçük döşemeler.
function SonucBlogu({ task, kokRef, teslim, medya, onayNotu, perms, arac, onMesaj }) {
  const onayBekliyor = task.status === "onay_bekliyor";
  const bitti = task.status === "tamamlandi";
  const iptalAmaTeslim = task.status === "iptal_edildi" && Boolean(teslim);
  if (!onayBekliyor && !bitti && !iptalAmaTeslim) return null;

  // Onay ne kadardır bekliyor? Geciken onay bu tek satırdan okunur.
  let bekleme = "";
  if (onayBekliyor && teslim?.created_at) {
    const gun = Math.floor((Date.now() - new Date(teslim.created_at).getTime()) / 86400000);
    bekleme = gun >= 1 ? ` · ${gun} gündür` : " · bugün";
  }

  const ton = bitti ? "onay" : iptalAmaTeslim ? "iptal" : "teslim";
  const Ikon = bitti ? CheckCircleIcon : iptalAmaTeslim ? ExclamationTriangleIcon : ClockIcon;
  const baslik = bitti ? "Onaylandı" : iptalAmaTeslim ? "Teslim edilen iş" : "Onay bekliyor";
  const altBaslik = bitti
    ? trDateTime(task.completed_at)
    : iptalAmaTeslim
    ? "Talep sonradan iptal edildi"
    : `Teslim edildi${bekleme}`;

  return (
    <Kart
      ton={ton}
      ikon={Ikon}
      baslik={baslik}
      kokRef={kokRef}
      etiketli="Sonuç"
      ekSinif="gk-sonuc"
      // FAZ-72: alt başlık SAĞ SLOTTAN başlığın ALTINA indi. Sağ slotta hem uzun
      // bir metin hem de düğme olunca başlık şeridi sarıyor ve kart "kaymış"
      // görünüyordu (kullanıcı 2026-07-30).
      altBaslik={altBaslik}
      sag={
        /* "Sohbette onayla" bir KOMUT değil YÖN LEVHASIDIR: görevi onaylamaz,
           sohbeti teslim mesajına götürür. Kumandanın tek yeri sohbet kalır. */
        onayBekliyor && perms.can_close_confirm ? (
          <button
            type="button"
            className="gk-gitchip"
            onClick={() => onMesaj(teslim?.message_id)}
            disabled={!teslim?.message_id}
          >
            <ChatBubbleOvalLeftEllipsisIcon className="gk-ic" /> Sohbette onayla →
          </button>
        ) : null
      }
    >
      <div className="gk-sonuc-govde">
        <div className="gk-sonuc-sol">
          <div className="gk-sonuc-kisi">
            <span className="gk-person-av gk-person-av--doer">
              {initials(teslim?.by_display)}
            </span>
            <div className="gk-sonuc-kisi-main">
              <div className="gk-sonuc-kisi-ad">{teslim?.by_display || "—"}</div>
              <div className="gk-sonuc-kisi-zaman">
                {trDateTime(teslim?.created_at)} · teslim etti
              </div>
            </div>
          </div>

          {teslim?.note ? (
            <p className="gk-sonuc-not">{teslim.note}</p>
          ) : (
            <p className="gk-sonuc-not gk-sonuc-not--bos">Teslim notu girilmemiş.</p>
          )}

          {onayBekliyor && !perms.can_close_confirm && (
            <div className="gk-sonuc-alt">
              <span className="gk-sonuc-etiket gk-sonuc-etiket--sessiz">Beklemede</span>
              <span className="gk-sonuc-aciklama">
                {task.created_by_display || "Talebi veren"} inceliyor.
              </span>
            </div>
          )}

          {bitti && onayNotu && (
            <div className="gk-sonuc-alt">
              <span className="gk-sonuc-etiket gk-sonuc-etiket--onay">Onay notu</span>
              <span className="gk-sonuc-aciklama">{onayNotu}</span>
            </div>
          )}

          {iptalAmaTeslim && task.cancel_reason && (
            <div className="gk-sonuc-alt">
              <span className="gk-sonuc-etiket gk-sonuc-etiket--iptal">İptal nedeni</span>
              <span className="gk-sonuc-aciklama">{task.cancel_reason}</span>
            </div>
          )}
        </div>

        {medya.length > 0 && (
          <div className="gk-sonuc-sag">
            <MedyaIzgarasi ogeler={medya} arac={arac} ekSinif="gk-ekgrid--dar" />
          </div>
        )}
      </div>
    </Kart>
  );
}

// ── ⑤ ZAMAN TÜNELİ — en yeni ÜSTTE, süzülmüş, sayaçlı ────────────────────
//
// Mesaj/soru/ek satırları backend'de süzülür (TIMELINE_GORUNUR); sayıları
// `event_counts` ile gelir. Başlıkta artık "(3) · 2 soru" gibi yığılma yok:
// sayı SAĞ SLOTTA rozet, özet onun yanında soluk bir not (ortak kart dili).
const GORUNEN_OLAY = 4;

function ZamanTuneliPanel({ task }) {
  const [acik, degistir] = useAkordeon("tunel", true);
  const [hepsi, setHepsi] = useState(false);
  const olaylar = useMemo(
    () =>
      [...(task.events || [])].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      ),
    [task.events]
  );
  if (olaylar.length === 0) return null;

  const sayac = task.event_counts || {};
  const ozet = [
    sayac.mesaj ? `${sayac.mesaj} mesaj` : null,
    sayac.soru ? `${sayac.soru} soru` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const gosterilen = hepsi ? olaylar : olaylar.slice(0, GORUNEN_OLAY);

  return (
    <Kart
      ikon={ClockIcon}
      baslik="Zaman tüneli"
      etiketli="Zaman tüneli"
      acik={acik}
      onAc={degistir}
      sag={
        <>
          {ozet && <span className="gk-kart-not">{ozet}</span>}
          <span className="gk-kart-sayi">{olaylar.length}</span>
        </>
      }
    >
      <ol className="gk-timeline">
        {gosterilen.map((e) => (
          <li className={`gk-tl-item gk-tl--${e.event_type}`} key={e.id}>
            <span className="gk-tl-dot" aria-hidden="true" />
            <div className="gk-tl-main">
              <div className="gk-tl-head">
                <span className="gk-tl-label">{e.event_label || e.event_type}</span>
                <span className="gk-tl-time">{trDateTime(e.created_at)}</span>
              </div>
              <div className="gk-tl-sub">{olaySatiri(e)}</div>
            </div>
          </li>
        ))}
      </ol>
      {olaylar.length > GORUNEN_OLAY && (
        <button type="button" className="gk-kart-more" onClick={() => setHepsi((v) => !v)}>
          {hepsi ? "Daha az göster" : `Tümünü göster (${olaylar.length})`}
        </button>
      )}
    </Kart>
  );
}

// Tünel satırının İKİNCİ satırı: aktör + kısa açıklama (tek satır, taşarsa "…").
function olaySatiri(e) {
  const p = e?.payload || {};
  const kisi = e.actor_display ? <b>{e.actor_display}</b> : null;
  if (e.event_type === "due_changed") {
    const eski = [p.from_date, p.from_time].filter(Boolean).join(" ") || "süresiz";
    const yeni = [p.to_date, p.to_time].filter(Boolean).join(" ") || "süresiz";
    return (
      <>
        {kisi}
        {kisi ? " · " : ""}
        {eski} → {yeni}
      </>
    );
  }
  const not = p.note || p.reason || "";
  return (
    <>
      {kisi}
      {kisi && not ? " · " : ""}
      {not ? `“${not}”` : ""}
    </>
  );
}

// ── ⑤ SOHBETTE PAYLAŞILANLAR ─────────────────────────────────────────────
//
// Yalnız SÜREÇ medyası burada. Brief künyede, teslim sonuç bloğunda durur.
// Gruplama kalktı (yön/tür başlıkları üçüncü bir görsel dil ekliyordu); tek
// ızgara, yön bilgisi döşemenin köşesindeki ok işaretinde.
const TUR_BASLIK = { gorsel: "Görsel", belge: "Belge", konum: "Konum" };

function PaylasilanlarPanel({ medya, arac }) {
  const [acik, degistir] = useAkordeon("belgelik", true);
  const toplam = medya.length;
  if (toplam === 0) return null;

  const ozet = ["konum", "gorsel", "belge"]
    .map((t) => {
      const n = medya.filter((m) => m.tur === t).length;
      return n ? `${n} ${TUR_BASLIK[t].toLocaleLowerCase("tr")}` : null;
    })
    .filter(Boolean)
    .join(" · ");

  return (
    <Kart
      ikon={PaperClipIcon}
      baslik="Sohbette paylaşılanlar"
      etiketli="Sohbette paylaşılanlar"
      acik={acik}
      onAc={degistir}
      sag={
        <>
          <span className="gk-kart-not">{ozet}</span>
          <span className="gk-kart-sayi">{toplam}</span>
        </>
      }
    >
      <MedyaIzgarasi ogeler={medya} arac={arac} />
    </Kart>
  );
}
